/**
 * State 4 — Premium user (active subscriber).
 * Goal: daily engagement, habit-building, community participation.
 * Two-column layout: 2fr left + 1fr right sidebar.
 * No upgrade prompts anywhere on this page.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DashboardUser } from './types';
import { HeroBanner } from './shared/HeroBanner';
import { CourseItem } from './shared/CourseItem';
import { CommunitySignals, CommunitySignal } from './shared/CommunitySignals';
import { StatChip } from './shared/StatChip';
import { XPBarChart } from './shared/XPBarChart';
import { TodayGoal, Goal } from './shared/TodayGoal';
import { AITip } from './shared/AITip';
import { ReadinessBar } from './shared/ReadinessBar';
import { ROUTES } from '../../utils/constants';


interface Props {
  user: DashboardUser;
}

const COMMUNITY_SIGNALS: CommunitySignal[] = [
  {
    name: 'Eric R.',
    initials: 'ER',
    avatarColor: '#5b6cff',
    action: 'shared a win in #small-business 🎉',
    timeAgo: '4 min ago',
    isNew: true,
  },
  {
    name: 'Paul W.',
    initials: 'PW',
    avatarColor: '#ff6b6b',
    action: 'asked a question in #support',
    timeAgo: '12 min ago',
  },
  {
    name: 'Jenny K.',
    initials: 'JK',
    avatarColor: '#f59e0b',
    action: 'posted in #marketing-ai',
    timeAgo: '31 min ago',
  },
];

const AI_TIP =
  "Try starting your ChatGPT prompts with 'You are an expert in…' — assigning a role gives you dramatically better results with less back-and-forth.";

export const DashState4Premium: React.FC<Props> = ({ user }) => {
  const [activeCourse, ...upNextCourses] = user.enrolledCourses;
  const streakOk = user.streakDays > 0;

  const todayIndex = (new Date().getDay() + 6) % 7; // 0=Mon … 6=Sun
  const xpToday = user.weeklyXP[todayIndex] ?? 0;
  const earnedTargetXP = xpToday >= 50;

  const todayGoals: Goal[] = [
    { label: 'Complete 1 lesson', done: user.completedLessonToday },
    // TODO: wire to community posts when data is available (no existing query for user posts today)
    { label: 'Post in the community', done: false },
    { label: 'Earn 50 XP today', done: earnedTargetXP },
  ];

  return (
    <div style={{ background: '#f7f8ff', minHeight: '100%' }}>
      <main className="container-custom py-10">
        {/* Hero */}
        <HeroBanner
          headline={`Welcome back, ${user.name}. Keep the momentum going.`}
          sub="Your personalised plan is ready. Pick up where you left off."
          animDelay={0}
        />

        {/* Mobile: Today's Goals immediately below hero */}
        <div className="block lg:hidden mb-6">
          <TodayGoal goals={todayGoals} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ══════════════════════════════════════════════════════════
              LEFT COLUMN — 2/3 width
          ══════════════════════════════════════════════════════════ */}
          <div className="lg:col-span-2 space-y-6">

            {/* ── CONTINUE LEARNING PLAN ─────────────────────────────── */}
            <div
              className="db-fade-up rounded-2xl p-6 bg-white"
              style={{ border: '1.5px solid #e8e8ee', animationDelay: '100ms' }}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-montserrat font-bold text-base text-neutral-800">
                  Continue your learning plan
                </h2>
                <Link
                  to={ROUTES.dashboardCourses}
                  className="font-open-sans text-sm font-semibold transition-colors hover:opacity-80"
                  style={{ color: '#5b6cff' }}
                >
                  View all →
                </Link>
              </div>

              <div className="space-y-3">
                {activeCourse && (
                  <CourseItem
                    course={activeCourse}
                    variant="active"
                  />
                )}
                {upNextCourses.slice(0, 2).map((course) => (
                  <CourseItem key={course.id} course={course} variant="default" />
                ))}
              </div>
            </div>

            {/* ── RECENTLY COMPLETED ──────────────────────────────────── */}
            <div
              className="db-fade-up rounded-2xl p-6 bg-white"
              style={{ border: '1.5px solid #e8e8ee', animationDelay: '200ms' }}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-montserrat font-bold text-base text-neutral-800">
                  Recently completed
                </h2>
                <Link
                  to={ROUTES.dashboardCoursesCompleted}
                  className="font-open-sans text-sm font-semibold transition-colors hover:opacity-80"
                  style={{ color: '#5b6cff' }}
                >
                  View All Courses →
                </Link>
              </div>
              {user.completedCourses.length > 0 ? (
                <div className="space-y-3">
                  {user.completedCourses.slice(0, 2).map((course) => (
                    <CourseItem key={course.id} course={course} variant="completed" />
                  ))}
                </div>
              ) : (
                <p className="font-open-sans text-sm text-neutral-500">
                  No completed courses yet —{' '}
                  <Link
                    to={ROUTES.courses}
                    className="font-semibold hover:opacity-80"
                    style={{ color: '#5b6cff' }}
                  >
                    start your first one!
                  </Link>
                </p>
              )}
            </div>

            {/* ── COMMUNITY ───────────────────────────────────────────── */}
            <div
              className="db-fade-up"
              style={{ animationDelay: '300ms' }}
            >
              <CommunitySignals
                signals={COMMUNITY_SIGNALS}
                title="💬 Live right now in your community"
                fullWidthCta={true}
              />
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════
              RIGHT SIDEBAR — 1/3 width
          ══════════════════════════════════════════════════════════ */}
          <div className="space-y-5">

            {/* Today's Goals — first card in sidebar, hidden on mobile (shown above hero on mobile) */}
            <div className="db-fade-up hidden lg:block" style={{ animationDelay: '100ms' }}>
              <TodayGoal goals={todayGoals} />
            </div>

            {/* AI Readiness Level — clickable, routes to /ai-readiness */}
            <div className="db-fade-up" style={{ animationDelay: '100ms' }}>
              <Link to={ROUTES.aiReadiness} className="block" style={{ textDecoration: 'none' }}>
                <ReadinessBar
                  level={`🟡 ${user.aiReadinessLevel}`}
                  pct={user.aiReadinessPct}
                  xpMin={30}
                  xpMax={60}
                  showViewDetails
                />
              </Link>
            </div>

            {/* 2×2 stat chips */}
            <div
              className="db-fade-up grid grid-cols-2 gap-3"
              style={{ animationDelay: '150ms' }}
            >
              <Link to={ROUTES.dashboardCoursesInProgress} style={{ textDecoration: 'none' }}>
                <StatChip
                  icon="📚"
                  iconBg="rgba(91,108,255,0.12)"
                  value={user.enrolledCourses.length}
                  label="In Progress"
                />
              </Link>
              <Link to={ROUTES.dashboardCoursesCompleted} style={{ textDecoration: 'none' }}>
                <StatChip
                  icon="✅"
                  iconBg="rgba(0,194,168,0.12)"
                  value={user.completedCourses.length}
                  label="Completed"
                />
              </Link>
              <Link to={ROUTES.aiReadiness} style={{ textDecoration: 'none' }}>
                <StatChip
                  icon="⚡"
                  iconBg="rgba(245,158,11,0.12)"
                  value={`${user.xp} XP`}
                  label="Total XP"
                />
              </Link>
              {/* Streak chip — hidden if 0, replaced with nudge */}
              {streakOk ? (
                <StatChip
                  icon="🔥"
                  iconBg="rgba(255,107,107,0.12)"
                  value={`${user.streakDays}d`}
                  label="Current Streak"
                />
              ) : (
                <div
                  className="flex items-center justify-center p-3 rounded-2xl text-center"
                  style={{ border: '1.5px solid #e8e8ee', background: '#fff' }}
                >
                  <p
                    className="font-open-sans text-xs font-semibold leading-snug"
                    style={{ color: '#5b6cff' }}
                  >
                    Start a streak today — just 15 minutes!
                  </p>
                </div>
              )}
            </div>

            {/* XP Bar Chart */}
            <div
              className="db-fade-up rounded-2xl p-5 bg-white"
              style={{ border: '1.5px solid #e8e8ee', animationDelay: '250ms' }}
            >
              <XPBarChart data={user.weeklyXP} />
            </div>

            {/* AI Tip */}
            <div className="db-fade-up" style={{ animationDelay: '300ms' }}>
              <AITip tip={AI_TIP} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
